import { useId } from 'react'
import { cn } from '@/lib/utils'
import { KIND_GROUPS, type AddKind } from './addKinds'
import { KindTile } from './fields'

/** The grouped "what are you adding?" list: a bottom sheet on phone, a side panel on desktop. */
export function AddChooser({
  selected,
  onPick,
  compact = false,
}: {
  selected?: AddKind | null
  onPick: (kind: AddKind) => void
  /** Desktop side panel: labels only, no hints. */
  compact?: boolean
}) {
  const baseId = useId()
  return (
    <div className="flex flex-col">
      {KIND_GROUPS.map((group, gi) => {
        const groupId = `${baseId}-g${gi}`
        return (
          <div key={group.label} role="group" aria-labelledby={groupId} className="flex flex-col">
            <p
              id={groupId}
              className="mx-1 mt-2.5 mb-1 text-[11px] font-semibold uppercase tracking-[0.08em] text-[var(--fl-text-3)]"
            >
              {group.label}
            </p>
            {group.kinds.map((k) => {
              const active = selected === k.kind
              const hintId = `${baseId}-${k.kind}-hint`
              return (
                <button
                  key={k.kind}
                  type="button"
                  aria-label={k.label}
                  aria-current={active || undefined}
                  aria-describedby={compact ? undefined : hintId}
                  onClick={() => onPick(k.kind)}
                  className={cn(
                    'flex items-center gap-3 rounded-xl px-1.5 text-left transition-colors',
                    compact ? 'min-h-11' : 'min-h-[52px] py-1.5',
                    active
                      ? 'bg-[var(--fl-primary-subtle)] text-[var(--fl-primary-text)]'
                      : 'hover:bg-[var(--fl-surface-hover)]'
                  )}
                >
                  <KindTile Icon={k.Icon} tone={k.tone} size={compact ? 'sm' : 'md'} />
                  <span className="min-w-0 flex-1">
                    <span className="block text-[15px] font-medium text-[var(--fl-text-1)]">
                      {k.label}
                    </span>
                    {!compact && (
                      <span id={hintId} className="block text-[12.5px] text-[var(--fl-text-2)]">
                        {k.hint}
                      </span>
                    )}
                  </span>
                </button>
              )
            })}
          </div>
        )
      })}
    </div>
  )
}
